/**
 * 用户行为统计页面
 * 展示用户登录和使用情况统计
 */
import React, { useState, useEffect } from 'react';
import { Card, Statistic, Table, Tag, message, Spin, Empty, Row, Col, DatePicker, Select, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { 
  ArrowLeftOutlined, 
  UserOutlined, 
  LoginOutlined, 
  BarChartOutlined,
  DesktopOutlined,
  MobileOutlined,
  TabletOutlined
} from '@ant-design/icons';
import api from '../services/api';
import './UserActivityStatistics.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const UserActivityStatistics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [loginStats, setLoginStats] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchOverview();
    fetchLoginStats();
    fetchUsageStats();
  }, [dateRange]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange && dateRange.length === 2) {
        params.startDate = moment(dateRange[0]).format('YYYY-MM-DD');
        params.endDate = moment(dateRange[1]).format('YYYY-MM-DD');
      }
      
      const response = await api.get('/statistics/overview', { params });
      if (response.data.success) {
        setOverview(response.data.data);
      }
    } catch (error) {
      console.error('获取统计概览失败:', error);
      message.error('获取统计概览失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginStats = async () => {
    try {
      const params = {};
      if (dateRange && dateRange.length === 2) {
        params.startDate = moment(dateRange[0]).format('YYYY-MM-DD');
        params.endDate = moment(dateRange[1]).format('YYYY-MM-DD');
      }
      
      const response = await api.get('/statistics/logins', { params });
      if (response.data.success) {
        setLoginStats(response.data.data);
      }
    } catch (error) {
      console.error('获取登录统计失败:', error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const params = {};
      if (dateRange && dateRange.length === 2) {
        params.startDate = moment(dateRange[0]).format('YYYY-MM-DD');
        params.endDate = moment(dateRange[1]).format('YYYY-MM-DD');
      }
      
      const response = await api.get('/statistics/usage', { params });
      if (response.data.success) {
        setUsageStats(response.data.data);
      }
    } catch (error) {
      console.error('获取使用统计失败:', error);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleRefresh = () => {
    fetchOverview();
    fetchLoginStats();
    fetchUsageStats();
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'desktop':
        return <DesktopOutlined />;
      case 'mobile':
        return <MobileOutlined />;
      case 'tablet':
        return <TabletOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  // 登录统计表格列
  const loginByUserColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: '登录次数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: '首次登录',
      dataIndex: 'firstLogin',
      key: 'firstLogin',
      render: (date) => formatDate(date),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date) => formatDate(date),
    },
  ];

  // 页面访问统计表格列
  const pageViewColumns = [
    {
      title: '页面路径',
      dataIndex: 'path',
      key: 'path',
    },
    {
      title: '访问次数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: '独立用户数',
      dataIndex: 'uniqueUserCount',
      key: 'uniqueUserCount',
      sorter: (a, b) => a.uniqueUserCount - b.uniqueUserCount,
    },
  ];

  return (
    <div className="user-activity-statistics">
      {/* Header */}
      <Card className="statistics-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ marginRight: 16 }}
            >
              返回
            </Button>
            <h1 style={{ margin: 0, display: 'inline' }}>用户行为统计</h1>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <RangePicker
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              placeholder={['开始日期', '结束日期']}
            />
            <Button onClick={handleRefresh}>刷新</Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 概览统计 */}
          {overview && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="总活动数"
                    value={overview.overview?.totalActivities || 0}
                    prefix={<BarChartOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="登录次数"
                    value={overview.overview?.loginCount || 0}
                    prefix={<LoginOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="活跃用户数"
                    value={overview.overview?.activeUserCount || 0}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <Row gutter={[16, 16]}>
            {/* 行为类型统计 */}
            {overview && overview.activitiesByType && (
              <Col xs={24} lg={12}>
                <Card title="行为类型统计" className="statistics-card">
                  <Table
                    dataSource={overview.activitiesByType.map((item, index) => ({
                      key: index,
                      actionType: item._id,
                      count: item.count,
                    }))}
                    columns={[
                      {
                        title: '行为类型',
                        dataIndex: 'actionType',
                        key: 'actionType',
                        render: (type) => {
                          const typeMap = {
                            login: '登录',
                            logout: '登出',
                            page_view: '页面访问',
                            api_call: 'API调用',
                            knowledge_view: '查看知识点',
                            session_create: '创建会议',
                            session_join: '参与会议',
                            answer_submit: '提交答案',
                            other: '其他',
                          };
                          return <Tag color="blue">{typeMap[type] || type}</Tag>;
                        },
                      },
                      {
                        title: '次数',
                        dataIndex: 'count',
                        key: 'count',
                        sorter: (a, b) => a.count - b.count,
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            )}

            {/* 设备类型统计 */}
            {overview && overview.deviceStats && (
              <Col xs={24} lg={12}>
                <Card title="设备类型统计" className="statistics-card">
                  <Table
                    dataSource={overview.deviceStats.map((item, index) => ({
                      key: index,
                      deviceType: item._id,
                      count: item.count,
                    }))}
                    columns={[
                      {
                        title: '设备类型',
                        dataIndex: 'deviceType',
                        key: 'deviceType',
                        render: (type) => {
                          const typeMap = {
                            desktop: '桌面',
                            mobile: '移动',
                            tablet: '平板',
                            unknown: '未知',
                          };
                          return (
                            <span>
                              {getDeviceIcon(type)} {typeMap[type] || type}
                            </span>
                          );
                        },
                      },
                      {
                        title: '次数',
                        dataIndex: 'count',
                        key: 'count',
                        sorter: (a, b) => a.count - b.count,
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            )}
          </Row>

          {/* 登录统计 */}
          {loginStats && loginStats.loginsByUser && (
            <Card title="用户登录统计" className="statistics-card" style={{ marginTop: 24 }}>
              <Table
                dataSource={loginStats.loginsByUser.map((item, index) => ({
                  key: index,
                  ...item,
                }))}
                columns={loginByUserColumns}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}

          {/* 使用情况统计 */}
          {usageStats && (
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              {usageStats.pageViews && (
                <Col xs={24} lg={12}>
                  <Card title="页面访问统计" className="statistics-card">
                    <Table
                      dataSource={usageStats.pageViews.map((item, index) => ({
                        key: index,
                        ...item,
                      }))}
                      columns={pageViewColumns}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  </Card>
                </Col>
              )}

              {usageStats.knowledgeViews && (
                <Col xs={24} lg={12}>
                  <Card title="知识点查看统计" className="statistics-card">
                    <Table
                      dataSource={usageStats.knowledgeViews.map((item, index) => ({
                        key: index,
                        ...item,
                      }))}
                      columns={[
                        {
                          title: '知识点',
                          dataIndex: 'knowledgeTitle',
                          key: 'knowledgeTitle',
                          ellipsis: true,
                        },
                        {
                          title: '查看次数',
                          dataIndex: 'count',
                          key: 'count',
                          sorter: (a, b) => a.count - b.count,
                        },
                        {
                          title: '独立用户数',
                          dataIndex: 'uniqueUserCount',
                          key: 'uniqueUserCount',
                        },
                      ]}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  </Card>
                </Col>
              )}
            </Row>
          )}

          {/* 最活跃用户 */}
          {overview && overview.topUsers && (
            <Card title="最活跃用户 (Top 10)" className="statistics-card" style={{ marginTop: 24 }}>
              <Table
                dataSource={overview.topUsers.map((item, index) => ({
                  key: index,
                  ...item,
                }))}
                columns={[
                  {
                    title: '用户名',
                    dataIndex: 'username',
                    key: 'username',
                  },
                  {
                    title: '姓名',
                    dataIndex: 'userName',
                    key: 'userName',
                  },
                  {
                    title: '活动次数',
                    dataIndex: 'count',
                    key: 'count',
                    sorter: (a, b) => a.count - b.count,
                  },
                  {
                    title: '最后活动时间',
                    dataIndex: 'lastActivity',
                    key: 'lastActivity',
                    render: (date) => formatDate(date),
                  },
                ]}
                pagination={false}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default UserActivityStatistics;
